import { __rest, __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx } from 'react/jsx-runtime';
import { createContext, useRef, useContext } from 'react';
import { useStore, createStore } from 'zustand';
import { useLlm } from './useLlm.js';

const createLlmViewStore = _ref => {
  let {
    threadId,
    dismissable = true,
    imperative = false,
    skipCustomIntro = false,
    idleTime = 8000,
    opened,
    embedded,
    portal,
    send,
    setEmbed
  } = _ref;
  const props = {
    opened: opened != null ? opened : embedded != null || threadId != null ? true : false,
    dismissable: embedded ? false : dismissable,
    imperative,
    skipCustomIntro: skipCustomIntro,
    embedded: embedded || false,
    portal: Object.assign({
      selector: '[data-llm-result]',
      replace: true
    }, portal),
    introed: skipCustomIntro ? true : false,
    idleTime: idleTime,
    hidden: false,
    fixed: false,
    idle: true,
    visible: false,
    scrollable: false,
    direction: 0
  };
  let scroller_ = undefined;
  const useStore = createStore((set, get) => {
    const onScrollerScroll = event => {
      const {
        direction,
        targetScroll,
        limit
      } = event;
      // console.log('onScrollerScroll', direction, targetScroll, limit);
      const scrolled = targetScroll > 100;
      const fixed = direction == -1 && scrolled;
      const hidden = direction == 1 && scrolled;
      const scrollable = limit - targetScroll > 100;
      const state = get();
      if (state.hidden !== hidden || state.fixed !== fixed || state.direction !== direction || state.scrollable !== scrollable) {
        set(state => ({
          hidden,
          fixed,
          direction,
          scrollable
        }));
      }
      // console.log('onScrollerScroll', event, event.progress, event.direction);
    };
    let to;
    const clearIdleTimeout = () => {
      if (to) {
        clearTimeout(to);
      }
      set(() => ({
        idle: false
      }));
    };
    const setIdleTimeout = () => {
      clearIdleTimeout();
      const {
        idleTime
      } = get();
      return setTimeout(() => {
        // console.log('idle');
        set(() => ({
          idle: true
        }));
      }, idleTime);
    };
    return Object.assign(Object.assign({}, props), {
      actions: {
        initView: () => __awaiter(void 0, void 0, void 0, function* () {
          get();
          const handleIdleTimes = () => {
            to = setIdleTimeout();
            let lastScrollTop = 0;
            const onScroll = () => {
              const scrollTop = window.scrollY || document.documentElement.scrollTop;
              let direction = 0;
              if (scrollTop > lastScrollTop) {
                direction = 1;
              } else if (scrollTop < lastScrollTop) {
                direction = -1;
              }
              lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
              if (direction === 1) {
                to = setIdleTimeout();
              }
            };
            const onResize = () => {
              if (document.scrollingElement && document.scrollingElement.scrollHeight === document.scrollingElement.offsetHeight) {
                clearIdleTimeout();
                lastScrollTop = 0;
              }
            };
            /*
            if ('ResizeObserver' in window) {
              const resizeObserver = new ResizeObserver((entries) => {
                onScroll();
              });
              resizeObserver.observe(document.scrollingElement as HTMLElement);
            }
            */
            window.addEventListener('scroll', onScroll);
            window.addEventListener('resize', onResize);
          };
          setTimeout(() => {
            set(() => ({
              idle: false
            }));
            handleIdleTimes();
          }, 2000);
        }),
        initIntersection: element => {
          // console.log('initIntersection', element);
          const handleIntersection = () => {
            // console.log('handleIntersection', element);
            if (!element) {
              return;
            }
            if ('IntersectionObserver' in window) {
              const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                  // console.log('IntersectionObserver', entry.target, entry.isIntersecting);
                  const state = get();
                  state.actions.setVisible(entry.isIntersecting);
                  if (embedded) {
                    setEmbed(entry.target, entry.isIntersecting);
                  }
                });
              }, {
                root: document,
                rootMargin: '50px',
                threshold: [0.01, 0.99]
              });
              observer.observe(element);
            } else {
              const state = get();
              state.actions.setVisible(true);
            }
          };
          handleIntersection();
        },
        send: (prompt, onMessage, onEnd) => __awaiter(void 0, void 0, void 0, function* () {
          yield send(prompt, () => {
            set({
              skipCustomIntro: true,
              opened: true
            });
            setTimeout(() => {
            }, 50);
          }, response => {
            if (typeof onMessage === 'function') {
              onMessage(response);
            }
          }, response => {
            if (typeof onEnd === 'function') {
              onEnd(response);
            }
          });
        }),
        open: () => {
          setIdleTimeout();
          set(state => ({
            opened: !state.opened
          }));
        },
        isOpened: () => {
          const {
            opened
          } = get();
          return opened;
        },
        setOpened: opened => {
          setIdleTimeout();
          set(state => ({
            opened
          }));
        },
        setIntroed: introed => {
          const state = get();
          introed ? state.actions.scrollerStart() : state.actions.scrollerStop();
          set(state => ({
            introed
          }));
        },
        setVisible: visible => set(state => ({
          visible
        })),
        setScrollable: scrollable => set(state => ({
          scrollable
        })),
        setScroller: scroller => {
          if (scroller_ !== scroller) {
            if (scroller_) {
              scroller_.off('scroll', onScrollerScroll);
            }
            scroller_ = scroller;
            if (scroller) {
              scroller.on('scroll', onScrollerScroll);
            }
          }
        },
        scrollerStop: () => {
          if (scroller_) {
            scroller_.stop();
          }
        },
        scrollerStart: () => {
          if (scroller_) {
            scroller_.start();
          }
        },
        scrollToBottom: () => {
          if (scroller_) {
            scroller_.scrollTo('bottom', {
              immediate: true
            });
          }
        }
      }
    });
  });
  return useStore;
};
const LlmViewContext = /*#__PURE__*/createContext(null);
function LlmViewProvider(_a) {
  var {
      children
    } = _a,
    props = __rest(_a, ["children"]);
  const {
    send,
    setEmbed
  } = useLlm(state => state.actions);
  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = createLlmViewStore(Object.assign(Object.assign({}, props), {
      send,
      setEmbed
    }));
  }
  return storeRef.current && jsx(LlmViewContext.Provider, {
    value: storeRef.current,
    children: children
  });
}
function useLlmView(selector) {
  const store = useContext(LlmViewContext);
  if (!store) throw new Error('Missing LlmContext.Provider in the tree');
  return useStore(store, selector);
}

export { LlmViewContext, LlmViewProvider, useLlmView };
//# sourceMappingURL=useLlmView.js.map
